import React, { Fragment, useState } from 'react';
import styles from './DiscourseHistory.module.scss';
import Tooltip from '../../../../Components/Popup/Tooltip';
import { useAppContext } from '../../../../Services/AppContext';
import { FormattedDateTimeWithSeconds } from '../../../../Components/FormattedDate';
import { joinClasses } from '../../../../Utils/className.util';
import FirstUpper from '../../../../Components/FirstUpper';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import useTranslation from '../../../../Hooks/useTranslation';
import Text from '../../../../Components/Base/Text/Text';
import DiscourseHistorySubItem from './DiscourseHistorySubItem';
import { useDiscourseContext } from '../../Services/DiscourseContext';
import { NOT_ALLOWED_ATTACHMENTS } from '../../Enums/discourseDocumentItemType.const';
import {
	getDiscourseSickLeaveDocumentId,
	getSickLeaveTreatmentUrl,
} from '../../Services/discourse.service';
import AntivirusCheck from '../../../../Components/Base/Loading/AntivirusCheck';
import { nl2br } from '../../../../Helpers/nl2br.helper';
import {
	DocumentationFile,
	CareClaim,
	DocumentItem,
} from '../../../../graphql/graphql';
import { prepareDocumentationFileForMediaViewer } from '../../../Ecard/Services/eCardFile.service';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import DiscourseItemVersionHistoryModal from '../DiscourseItemVersionHistory/DiscourseItemVersionHistoryModal';
import {
	findDiscourseTravelAttachment,
	formatDiscourseTravelDistance,
} from '../DiscourseTravel/discourseTravel.helpers';

export interface DiscourseItem {
	id: string;
	type?: string;
	createdAt: Date;
	createdBy: {
		profile: {
			fullName: string;
		};
	};
	items: DiscourseSubItemType[];
	file: {
		avResultText: string;
	};
	documentationFile: DocumentationFile;
	careClaims?: CareClaim[];
	previousVersionDocumentId?: string;
}

export interface DiscourseBaseItem {
	patientId: string;
	virtualPatientId: string;
	openCareClaimModal: (
		discourseId: string,
		discourseMainDiagnosis?: DocumentItem,
	) => void;
	reloadData: () => void;
}

interface DiscourseHistoryItemProps extends DiscourseBaseItem {
	item: DiscourseItem;
	handleEdit?: (itemId: any) => void;
	key: string;
	onShowInCalendar?: any;
	allowVersionHistory?: boolean;
	allowMedicalProcedures?: boolean;
}

export interface DiscourseItemExtra {
	id?: string;
	text?: {
		label?: string;
		value?: string;
	};
	title?: string;
	sentToPatient?: boolean;
	subType?: string;
	folderId?: string;
}

export interface DiscourseSubItemType {
	id: string;
	type: string;
	extra: DiscourseItemExtra;
	onShowInCalendar?: any;
}

const DiscourseHistoryItem: React.FC<DiscourseHistoryItemProps> = ({
	item,
	handleEdit,
	patientId,
	onShowInCalendar,
	openCareClaimModal,
	reloadData,
	virtualPatientId,
	allowVersionHistory = true,
	allowMedicalProcedures = true,
}) => {
	const { activeDiscourseId } = useDiscourseContext();
	const { socketUrl } = useAppContext();
	const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
	const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
	const { t } = useTranslation();
	const { isAllowed } = useAclContext();

	const mainDiagnosis = item.items.find(
		subItem => subItem?.extra?.subType === 'main_diagnosis',
	);

	const discourseName =
		(
			item.items?.find(subItem => subItem.type === 'name')?.extra?.text as
				| string
				| undefined
		)?.trim() ?? '';

	const discourseTravel = findDiscourseTravelAttachment(item.items);

	const datetimeText = item.items?.find(subItem => subItem.type === 'datetime')
		?.extra?.text as string | undefined;
	const headerDatetime: Date | string = datetimeText
		? new Date(datetimeText)
		: item.createdAt;

	const editButton = handleEdit && (
		<Tooltip
			trigger={
				<div className={styles.edit} onClick={() => handleEdit(item.id)} />
			}
			position='top center'>
			<Text tx='base.shared.edit' firstUpper />
		</Tooltip>
	);

	const versionsButton = allowVersionHistory &&
		item.previousVersionDocumentId && (
			<Tooltip
				trigger={
					<div
						className={styles.history}
						onClick={() => setIsVersionHistoryOpen(true)}
					/>
				}
				position='top center'>
				<Text tx='base.versionHistory' firstUpper />
			</Tooltip>
		);

	const medicalProceduresButton = allowMedicalProcedures &&
		isAllowed('document_care_claim') && (
			<LinkButton
				title='component.discourse.editMedicalProcedures'
				theme={Theme.BLUE}
				onClick={() => openCareClaimModal(item.id, mainDiagnosis)}
			/>
		);

	const discourseSickLeaveDocumentId = getDiscourseSickLeaveDocumentId(
		item.items,
	);

	const sickLeaveButton = discourseSickLeaveDocumentId && (
		<LinkButton
			title='component.discourse.viewSickLeave'
			theme={Theme.BLUE}
			onClick={() => {
				window.location.href = getSickLeaveTreatmentUrl(
					virtualPatientId,
					discourseSickLeaveDocumentId,
				);
			}}
		/>
	);

	const onCloseMediaViewer = (forceReload?: boolean) => {
		setMediaViewerOpened(false);
		if (forceReload) {
			reloadData();
		}
	};

	return (
		<div
			className={joinClasses([
				styles.item,
				item.id === activeDiscourseId && styles.editActive,
			])}>
			<div className={styles.header}>
				<div className={styles.date}>
					<FormattedDateTimeWithSeconds datetime={headerDatetime} />
				</div>
				<div className={styles.doctorsName}>
					<span>
						<FirstUpper>{t('base.wrote')}</FirstUpper>:
					</span>{' '}
					{item.createdBy?.profile?.fullName}
				</div>
			</div>

			<div className={styles.content}>
				<div className={styles.text}>
					{discourseName ? (
						<div className={styles.diagnose}>
							<span>
								{t('component.discourse.recordNameLabel')}
								{': '}
							</span>
							{discourseName}
						</div>
					) : null}
					<div className={styles.diagnose}>
						{mainDiagnosis && (
							<>
								<span>
									{t('component.discourse.mainDiagnosis')}
									{': '}
								</span>
								{mainDiagnosis?.extra?.text?.label}
							</>
						)}
					</div>
					{item.items
						.filter(
							subItem =>
								subItem.type === 'diagnosis' &&
								subItem?.extra?.subType === 'secondary_diagnosis',
						)
						.map((subItem: any, index) => {
							return (
								<div key={index} className={styles.diagnose}>
									<span>
										{t('component.discourse.secondaryDiagnosis')}
										{': '}
									</span>
									{subItem?.extra?.text?.label}
								</div>
							);
						})}
					{item.items
						.filter(subItem => subItem.type === 'content')
						.map((subItem, index) => {
							return (
								<div key={index} className={styles.mainContent}>
									{nl2br(subItem.extra?.text as string)}
								</div>
							);
						})}
					{(item.careClaims?.length || 0) > 0 && (
						<div className={styles.careClaims}>
							<span className={styles.careClaimsLabel}>
								{t('component.discourse.medicalProcedures')}:
							</span>
							<div className={styles.careClaimItems}>
								{item.careClaims?.map((careClaim, index) => {
									const key = careClaim?.id || index;

									if (!careClaim?.medicalProcedure) {
										return <Fragment key={key} />;
									}

									return (
										<span key={key}>
											{index > 0 && ', '}
											{careClaim.medicalProcedure.formattedName}
										</span>
									);
								})}
							</div>
						</div>
					)}
					{discourseTravel && (
						<div className={styles.careClaims}>
							<span className={styles.careClaimsLabel}>
								{t('component.discourse.travel')}:
							</span>
							<div className={styles.careClaimItems}>
								{formatDiscourseTravelDistance(discourseTravel)}
							</div>
						</div>
					)}
				</div>
				{item?.file ? (
					<div className={styles.controls}>
						{item?.file?.avResultText === 'clean' ? (
							<>
								<Tooltip
									trigger={
										<div
											className={styles.print}
											onClick={() => setMediaViewerOpened(true)}
										/>
									}
									position='top center'>
									<div>{t('base.print')}</div>
								</Tooltip>
								{versionsButton}
								{editButton}
								{sickLeaveButton}
								{medicalProceduresButton}
							</>
						) : (
							<div>
								<AntivirusCheck inRow={true} />
							</div>
						)}
					</div>
				) : (
					<div className={styles.controls}>
						{versionsButton}
						{editButton}
						{sickLeaveButton}
					</div>
				)}
			</div>
			<div className={styles.attachment}>
				{item.items
					.filter(
						subItem =>
							!NOT_ALLOWED_ATTACHMENTS.some(type => type === subItem.type),
					)
					.map(subItem => (
						<DiscourseHistorySubItem
							item={subItem}
							key={subItem.id}
							patientId={patientId}
							virtualPatientId={virtualPatientId}
							onShowInCalendar={onShowInCalendar}
						/>
					))}
			</div>
			{mediaViewerOpened && item.documentationFile && (
				<MediaViewerModal
					opened
					onClose={onCloseMediaViewer}
					file={prepareDocumentationFileForMediaViewer(
						item.documentationFile,
						socketUrl,
					)}
					isSignable={item.documentationFile.canBeSigned || false}
				/>
			)}
			{isVersionHistoryOpen && (
				<DiscourseItemVersionHistoryModal
					isOpen
					onClose={() => setIsVersionHistoryOpen(false)}
					discourseId={item.id}
					patientId={patientId}
					virtualPatientId={virtualPatientId}
					openCareClaimModal={openCareClaimModal}
					reloadData={reloadData}
				/>
			)}
		</div>
	);
};

export default DiscourseHistoryItem;
